<template>
  <div>
    <slot v-bind:open="open" />

    <Modal :show="show" @closeModal="close" size="md">
      <div class="hidden sm:block">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          Customer Handover/Delivery Details
        </h3>
      </div>
      <div class="mt-6 sm:mt-5">
        <!-- Customer contact -->
        <div class="sm:border-t sm:border-gray-200 sm:pt-5">
          <div role="group">
            <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline mb-4">
              <div>
                <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                  Customer Contact Number
                </div>
              </div>
              <div class="mt-4 sm:mt-0 sm:col-span-2">
                <div class="max-w-xs">
                  <text-input v-model="form.customer_contact_number" :disabled="isLocked" :errors="form.errors.customer_contact_number"></text-input>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="sm:border-t sm:border-gray-200 sm:pt-5">
          <div role="group">
            <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
              <div>
                <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                  Delivery Method
                </div>
              </div>
              <div class="mt-4 sm:mt-0 sm:col-span-2">
                <div class="max-w-xs">
                  <select-input :disabled="isLocked" v-model="form.customer_to_collect">
                    <option :value="false">To be Delivered</option>
                    <option :value="true">Collection by Customer</option>
                  </select-input>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-if="!form.customer_to_collect">

          <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Location
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-lg">
                    <select-input :disabled="isLocked" :errors="$page.props.errors.delivery_location" v-model="form.delivery_location">
                      <option v-for="(label, value) in locations" :value="value">{{ label }}</option>
                    </select-input>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="form.delivery_location !== 'island'" class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Address
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-lg">
                    <textarea-input :disabled="isLocked" :errors="$page.props.errors.delivery_address" v-model="form.delivery_address"></textarea-input>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-else>


            <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Vessel Name
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <delivery-to-boat :disabled="isLocked" @change="updateSelectedBoatID" :boatId="form.boat_id" :boat-name="form.vessel" :boat-location="form.vessel_location" :errors="$page.props.errors.vessel"></delivery-to-boat>
                  </div>
                </div>
              </div>
            </div>

            <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
              <div role="group">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                  <div>
                    <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                      Vessel Location
                    </div>
                  </div>
                  <div class="mt-4 sm:mt-0 sm:col-span-2">
                    <div class="max-w-lg">
                      <select-input :disabled="isLocked || !canAdmin('update-orders')" :errors="$page.props.errors.vessel_location" v-model="form.vessel_location">
                        <option v-for="jetty in vesselLocations.island" :value="jetty">{{ jetty }}</option>
                      </select-input>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </template>
        </template>

        <div class="mt-6 sm:mt-5">
          <div class="sm:border-t sm:border-gray-200 sm:pt-5">
            <div role="group">
              <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
                <div>
                  <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                    Delivery/Handover Date
                  </div>
                </div>
                <div class="mt-4 sm:mt-0 sm:col-span-2">
                  <div class="max-w-xs">
                    <date-input v-model="form.customer_handover_date" :disabled="isLocked" :errors="$page.props.errors.customer_handover_date"></date-input>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="sm:border-t sm:border-gray-200 sm:pt-5 mt-4">
          <div role="group">
            <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-baseline">
              <div>
                <div class="text-base leading-6 font-medium text-gray-900 sm:text-sm sm:leading-5 sm:text-gray-700">
                  Delivery Contact Number
                </div>
              </div>
              <div class="mt-4 sm:mt-0 sm:col-span-2">
                <div class="max-w-lg flex items-center gap-2">
                  <text-input :disabled="isLocked" :errors="$page.props.errors.delivery_contact_number" v-model="form.delivery_contact_number" />
                  <a v-if="form.delivery_contact_number" :href="`tel:${form.delivery_contact_number}`" class="text-sm leading-5 text-gray-500 hover:text-gray-900 p-3">
                    <Icon name="phone" class="h-4 w-4 mx-1" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div class="mt-4">
        <div class="pt-4 flex w-full items-center justify-between">
          <div>
            <small v-if="form.isDirty" class="text-yellow-600">You have unsaved changes! <a href="javascript:void(0)" @click="form.reset()" class="text-blue-500">Discard?</a></small>
          </div>
          <div v-if="canAdmin('update-orders') || canAdmin('update-order-item-location')" class="flex gap-x-3">
            <button class="btn-secondary" type="button" @click="close">Cancel</button>
            <LoadingButton
              class="btn-primary"
              :loading="form.processing"
              :disabled="isLocked || form.processing || !form.isDirty"
              @click="updateOrder"
            >
              Update
            </LoadingButton>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>

import Modal from "../../../../Shared/Modal.vue";
import {mapFormValue} from "../../../../Utils";
import LoadingButton from "../../../../Shared/LoadingButton.vue";
import Icon from "../../../../Shared/Icon.vue";
import TextInput from "../../../../Shared/TextInput.vue";
import DateInput from "../../../../Shared/DateInput.vue";
import SelectInput from "../../../../Shared/SelectInput.vue";
import DeliveryToBoat from "../DeliveryToBoat.vue";
import TextareaInput from "../../../../Shared/TextareaInput.vue";
import {debounce} from "lodash";

export default {
  name: 'UpdateDeliveryDetailsModal',
  components: {TextareaInput, DeliveryToBoat, SelectInput, DateInput, TextInput, Icon, LoadingButton, Modal},

  props: {
    order: Object,
    isLocked: Boolean,
    locations: Object,
    vesselLocations: Object,
  },

  data() {
    return {
      modalPersistKey: `delivery-details-modal-${this.order.id}`,
      form: this.$inertia.form({
        customer_to_collect: this.order.customer_to_collect || false,
        delivery_location: this.order.delivery_location || null,
        delivery_address: this.order.delivery_address || null,
        delivery_contact_number: this.order.delivery_contact_number || null,
        customer_handover_date: this.order.customer_handover_date || null,
        customer_contact_number: this.order.customer_contact_number || null,
        boat_id: this.order.boat_id || null,
        vessel: this.order.vessel || null,
        vessel_location: this.order.vessel_location || null,
      }),

      show: false,
    }
  },

  methods: {
    open() {
      this.show = true
      const state = {
        modalState: 'open',
        form: null,
      }
      localStorage.setItem(this.modalPersistKey, JSON.stringify(state));
      const url = new URL(window.location);
      url.searchParams.set('subAction', 'edit-details');

      window.history.pushState({}, '', url);
    },

    close() {
      this.show = false
      this.form.reset()
      localStorage.removeItem(this.modalPersistKey)
      const url = new URL(window.location);
      url.searchParams.delete('subAction');

      window.history.replaceState({}, '', url);

    },

    updateOrder() {
      if (this.canAdmin('update-orders') || this.canAdmin('update-order-item-location')) {
        this.form.put(this.route('customer.orders.fulfilment-details.update', this.order.id), {
          preserveState: 'error',
          onSuccess: () => this.close()
        })
      }
    },

    updateSelectedBoatID(boatSelected) {
      this.form.boat_id = boatSelected.id;
      this.form.vessel = boatSelected.name;
    },

    restorePersistantState() {
      const params = new URLSearchParams(window.location.search);

      if (params.get('subAction') === 'edit-details') {
        const state = localStorage.getItem(this.modalPersistKey);
        if (state) {
          const { form } = JSON.parse(state);
          if (form) {
            mapFormValue(this.form, form);
          }
        }
        this.open();
      }
    }
  },

  watch: {
    form: {
      handler: debounce(function (formState) {
          const state = {
            modalState: 'open',
            form: this.form.data(),
          };
          localStorage.setItem(this.modalPersistKey, JSON.stringify(state));

      }, 300),
      deep: true,
    },
  },

  mounted() {
    this.restorePersistantState()

  }
}
</script>
